/**
 * The one `ApiClient` this app uses.
 *
 * Everything goes through the generated client (CLAUDE.md §7: the OpenAPI
 * spec and its typed client are the contract, and CI fails on drift). What is
 * added here is the transport policy the generator has no opinion about:
 *
 *  - a single-flight token refresh, so ten parallel dashboard queries firing
 *    one second before expiry produce one `POST /auth/refresh`, not ten — and
 *    nine of them do not race the tenth's rotation into a revoked-token 401;
 *  - one retry after a 401, in case the token expired between the freshness
 *    check and the server reading it;
 *  - recognising `403 ERR_MFA_REQUIRED` (§6 makes MFA mandatory in this realm)
 *    and handing it to the app, which routes the operator into enrolment
 *    rather than showing them a permission error they cannot act on;
 *  - answering `403 ERR_STEP_UP_REQUIRED` (§6/§13) by prompting for a one-time
 *    code and replaying the request, so a guarded action completes instead of
 *    dying on a refusal the operator has no way to satisfy.
 */

import { adminAuthStepUp, ApiClient } from '@erpx/ui/api/generated'

import {
  accessToken,
  expiresSoon,
  getRefresher,
  notifyMfaRequired,
  notifySessionLost,
  session,
  setSession,
} from './session'
import { stepUp, stepUpReason } from './stepup'

/**
 * The generated client builds request URLs with `new URL(baseUrl + path)`, so
 * the base has to be absolute. Same-origin is the default — in development
 * Vite proxies `/admin/api` to the API (see vite.config.ts) and in production
 * nginx sits in front of both — and `VITE_API_BASE_URL` overrides it for the
 * split-origin case.
 */
function resolveBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL
  if (typeof configured === 'string' && configured.trim() !== '') return configured.trim()
  if (typeof window !== 'undefined') return window.location.origin
  return 'http://localhost'
}

const BASE_URL = resolveBaseUrl()

/**
 * Endpoints that must never trigger a refresh — they *are* the token flow.
 *
 * `/auth/step-up` is here for a sharper reason than the others: it answers a
 * refused code with 401, and retrying would re-present a one-time code the
 * server has already spent, drawing a second time on the lockout budget it
 * shares with login. Requests to these paths still carry the bearer token the
 * generated client put on them; only the retry behaviour is skipped.
 */
const AUTH_PATHS = [
  '/admin/api/v1/auth/login',
  '/admin/api/v1/auth/refresh',
  '/admin/api/v1/auth/accept-invite',
  '/admin/api/v1/auth/forgot-password',
  '/admin/api/v1/auth/reset-password',
  '/admin/api/v1/auth/step-up',
]

function isAuthPath(url: string): boolean {
  return AUTH_PATHS.some((path) => url.includes(path))
}

/** The in-flight refresh, shared by every caller that arrives while it runs. */
let inFlight: Promise<string | null> | null = null

export function refreshOnce(): Promise<string | null> {
  if (inFlight !== null) return inFlight
  const refresher = getRefresher()
  if (refresher === null) return Promise.resolve(null)

  inFlight = refresher()
    .catch(() => null)
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

function withAuthorization(init: RequestInit | undefined, token: string | null): RequestInit {
  const headers = new Headers(init?.headers)
  if (token === null) headers.delete('Authorization')
  else headers.set('Authorization', `Bearer ${token}`)
  return { ...init, headers }
}

/** The machine code on a 403, or `''` when the body is not problem+json. */
async function forbiddenCode(response: Response): Promise<string> {
  if (response.status !== 403) return ''
  try {
    const body: unknown = await response.clone().json()
    if (typeof body !== 'object' || body === null) return ''
    const code = (body as { code?: unknown }).code
    return typeof code === 'string' ? code : ''
  } catch {
    return ''
  }
}

async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url

  if (isAuthPath(url)) return fetch(input, init)

  let token = accessToken()
  if (token !== null && expiresSoon()) {
    token = (await refreshOnce()) ?? token
  }

  let response = await fetch(input, withAuthorization(init, token))

  if (response.status === 401 && session.value !== null) {
    const fresh = await refreshOnce()
    if (fresh === null) {
      notifySessionLost()
      return response
    }
    response = await fetch(input, withAuthorization(init, fresh))
  }

  let code = await forbiddenCode(response)

  if (code === 'ERR_STEP_UP_REQUIRED') {
    // The operator proves who they are, and then the thing they pressed the
    // button for happens. `init` is reused verbatim — body and all — so nothing
    // they filled in has to be re-entered.
    if ((await stepUp.challenge(stepUpReason(url))) === 'granted') {
      response = await fetch(input, withAuthorization(init, accessToken()))
      code = await forbiddenCode(response)
    }
  }

  if (code === 'ERR_MFA_REQUIRED') notifyMfaRequired()

  return response
}

export const client = new ApiClient({
  baseUrl: BASE_URL,
  getToken: accessToken,
  fetch: authFetch,
})

/**
 * A client that never refreshes — used by the login and refresh calls
 * themselves, which cannot depend on the session they are establishing.
 *
 * No `fetch` override is needed: `ApiClient` itself now falls back to a
 * closure-wrapped global fetch when none is supplied, so the bare default is
 * safe to call as a browser method.
 */
export const bareClient = new ApiClient({
  baseUrl: BASE_URL,
  getToken: accessToken,
})

/**
 * Teach the broker how to answer a challenge in the platform realm.
 *
 * `bareClient` deliberately: a refused code comes back 401, and the refreshing
 * client would re-send a one-time code the server has already burned. Only the
 * access token changes — `POST /auth/step-up` leaves the refresh token alone,
 * because the step-up is stamped on the session and travels with the next
 * rotation on its own.
 */
stepUp.setVerifier(async (code: string) => {
  const result = await adminAuthStepUp(bareClient, { code })
  const current = session.value
  if (current === null) return
  setSession({ ...current, accessToken: result.accessToken, expiresAt: result.expiresAt })
})
